import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Btn, Card, Field, Input, Select, StatusChip, Notice } from "../components/kit";
import { ConfirmDialog, Loading } from "../components/common";
import { showSimulated } from "../lib/demo";
import { api, newClientId, parseError } from "../lib/api";
import { fmtDateTime } from "../lib/format";

export function AccessPanel({ dogId }) {
  const { t } = useTranslation();
  const [d, setD] = useState(null);
  const [inv, setInv] = useState({ email: "", language: "en" });
  const [cid, setCid] = useState(newClientId());
  const [revoke, setRevoke] = useState(null);
  const [withdraw, setWithdraw] = useState(null);
  const [confirmInvite, setConfirmInvite] = useState(false);
  const [assignee, setAssignee] = useState("");
  const load = useCallback(() => api.get(`/dogs/${dogId}/access`).then((r) => setD(r.data)), [dogId]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loading />;

  const sendInvite = async () => {
    try {
      const { data } = await api.post(`/dogs/${dogId}/owner-invitations`, { ...inv, client_id: cid });
      if (data.invitation?.simulated) showSimulated([data.invitation.preview]);
      else if (data.email_sent) toast.success(t("access.invite_sent")); else toast.warning(t("access.invite_email_failed"));
      setInv({ email: "", language: "en" });
      setCid(newClientId());
      load();
    } catch (e) { throw new Error(t(`err.${parseError(e).code}`)); }
  };
  const assign = async () => {
    try { await api.post(`/dogs/${dogId}/assign`, { user_id: assignee }); toast.success(t("access.assigned")); setAssignee(""); load(); }
    catch (e) { toast.error(t(`err.${parseError(e).code}`)); }
  };
  const active = d.grants.filter((g) => g.status === "active");
  const revoked = d.grants.filter((g) => g.status === "revoked");
  const pending = d.invitations.filter((i) => i.status === "pending");
  const past = d.invitations.filter((i) => i.status !== "pending");
  return (
    <div className="grid gap-5 lg:grid-cols-2" data-testid="access-panel">
      <Card>
        <h2 className="mb-3 text-lg font-bold">{t("access.current")}</h2>
        <ul className="space-y-2">
          <li className="rounded-xl border bg-white p-3 text-sm"><b>{d.responsible.name}</b> — {t("access.kind_responsible")}</li>
          {active.map((g) => (
            <li key={g.id} data-testid={`grant-${g.kind}`} className="flex flex-wrap items-center gap-2 rounded-xl border bg-white p-3 text-sm">
              <b>{g.name}</b> — {t(`access.kind_${g.kind}`)} {g.language && g.kind === "owner" && `· ${t(`lang.${g.language}`)}`}
              <span className="text-muted-foreground">{t("access.since", { date: fmtDateTime(g.granted_at) })}</span>
              {d.can_manage && <Btn variant="danger" className="ml-auto min-h-[40px]" testid={`revoke-grant-btn-${g.id}`} onClick={() => setRevoke(g)}>{t("access.revoke")}</Btn>}
            </li>
          ))}
        </ul>
        {d.can_manage && d.eligible_members.length > 0 && (
          <div className="mt-4 flex flex-wrap items-end gap-2">
            <Field label={t("access.assign_member")} htmlFor="assign-sel">
              <Select id="assign-sel" data-testid="assign-member-select" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
                <option value="">{t("common.choose")}</option>
                {d.eligible_members.filter((m) => !active.some((g) => g.user_id === m.user_id)).map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
              </Select>
            </Field>
            <Btn variant="sage" disabled={!assignee} testid="assign-member-btn" onClick={assign}>{t("access.assign")}</Btn>
          </div>
        )}
      </Card>
      <Card>
        <h2 className="mb-3 text-lg font-bold">{t("access.invite_owner")}</h2>
        <div className="grid gap-3 sm:grid-cols-[1fr_160px]">
          <Field label={t("field.email")} htmlFor="inv-email"><Input id="inv-email" type="email" data-testid="owner-invite-email-input" value={inv.email} onChange={(e) => setInv({ ...inv, email: e.target.value })} /></Field>
          <Field label={t("access.owner_language")} htmlFor="inv-lang">
            <Select id="inv-lang" data-testid="owner-invite-language-select" value={inv.language} onChange={(e) => setInv({ ...inv, language: e.target.value })}><option value="en">English</option><option value="ru">Русский</option></Select>
          </Field>
        </div>
        <Btn className="mt-3" disabled={!inv.email.includes("@")} testid="owner-invite-btn" onClick={() => setConfirmInvite(true)}>{t("access.send_invite")}</Btn>
        <h3 className="mb-2 mt-6 font-bold">{t("access.pending")}</h3>
        {pending.length === 0 && <p className="text-sm text-muted-foreground">{t("access.no_pending")}</p>}
        <ul className="space-y-2">
          {pending.map((i) => (
            <li key={i.id} data-testid={`invitation-${i.id}`} className="flex flex-wrap items-center gap-2 rounded-xl border bg-white p-3 text-sm">
              <b>{i.email}</b><StatusChip status="pending" /><span className="text-muted-foreground">{t("access.expires", { date: fmtDateTime(i.expires_at) })}</span>
              <Btn variant="outline" className="ml-auto min-h-[40px]" testid={`withdraw-invite-btn-${i.id}`} onClick={() => setWithdraw(i)}>{t("access.withdraw")}</Btn>
            </li>
          ))}
        </ul>
      </Card>
      <Card className="lg:col-span-2">
        <h2 className="mb-3 text-lg font-bold">{t("access.past")}</h2>
        {revoked.length + past.length === 0 && <p className="text-sm text-muted-foreground">{t("access.no_past")}</p>}
        <ul className="space-y-2" data-testid="access-past-list">
          {revoked.map((g) => (
            <li key={g.id} className="flex flex-wrap items-center gap-2 rounded-xl border bg-white p-3 text-sm">
              <b>{g.name}</b> — {t(`access.kind_${g.kind}`)} <StatusChip status="revoked" />
              <span>{fmtDateTime(g.revoked_at)} · {g.revoked_by_name}</span><span className="w-full text-muted-foreground">{t("common.reason")}: {g.revoke_reason}</span>
            </li>
          ))}
          {past.map((i) => (
            <li key={i.id} className="flex flex-wrap items-center gap-2 rounded-xl border bg-white p-3 text-sm">
              <b>{i.email}</b> — {t("access.invitation")} <StatusChip status={i.status} />
              <span>{fmtDateTime(i.withdrawn_at || i.accepted_at || i.expires_at)}</span>
            </li>
          ))}
        </ul>
      </Card>
      <ConfirmDialog open={confirmInvite} onOpenChange={setConfirmInvite} title={t("access.send_invite")} recipient={`${inv.email} · ${t(`lang.${inv.language}`)}`}
        consequence={t("access.invite_consequence")} confirmLabel={t("access.send_invite")} onConfirm={sendInvite} testid="invite-confirm-dialog" />
      <ConfirmDialog open={!!revoke} onOpenChange={() => setRevoke(null)} title={t("access.revoke_title")} recipient={revoke?.name} danger
        consequence={t("access.revoke_consequence")} reasonLabel={t("common.reason")} reasonRequired confirmLabel={t("access.revoke")} testid="revoke-confirm-dialog"
        onConfirm={async (reason) => { await api.post(`/grants/${revoke.id}/revoke`, { reason }); toast.success(t("access.revoked")); load(); }} />
      <ConfirmDialog open={!!withdraw} onOpenChange={() => setWithdraw(null)} title={t("access.withdraw_title")} recipient={withdraw?.email}
        consequence={t("access.withdraw_consequence")} reasonLabel={t("common.reason_optional")} confirmLabel={t("access.withdraw")} testid="withdraw-confirm-dialog"
        onConfirm={async (reason) => { await api.post(`/invitations/id/${withdraw.id}/withdraw`, { reason }); load(); }} />
      <div className="lg:col-span-2"><Notice>{t("access.least_privilege")}</Notice></div>
    </div>
  );
}
